import * as React from "react"
import { DocumentStatus, FormatClass } from "@digits-graphql/frontend/graphql-bearer"
import { SvgDownload01 } from "@digits-shared/components/SVGIcons/line/Download01.svg"
import { SvgTrash01 } from "@digits-shared/components/SVGIcons/line/Trash01.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import {
  EllipsisMenuTrigger,
  Menu,
  MenuContent,
  MenuItem,
  MenuItemSeparator,
} from "@digits-shared/DesignSystem/Menu"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import fileHelper from "@digits-shared/helpers/fileHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import styled from "styled-components"
import { type VaultFile } from "src/frontend/components/OS/Applications/Vault/types"
import { useDownloadVaultFile } from "src/frontend/components/OS/Applications/Vault/useDownloadVaultFile"
import { DeleteFileConfirm } from "src/frontend/components/OS/Applications/Vault/VaultBrowser/DeleteFileButton"
import { useVaultContext } from "src/frontend/components/OS/Applications/Vault/VaultBrowser/state/VaultContext"
import { fileTypeFor } from "src/frontend/components/OS/Applications/Vault/vaultMime"
import { documentDownloadUrl } from "src/frontend/components/OS/Shared/DocumentViewer/helpers"
import { useUserSettings } from "src/frontend/hooks/useUserSettings"

/*
  STYLES
*/

const StyledMenuTrigger = styled(EllipsisMenuTrigger)<{ $hidden: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 0;
  filter: drop-shadow(0px 2px 12px rgba(0, 0, 0, 0.2));

  &.visible {
    opacity: 1;
  }
`

/*
  INTERFACES
*/

/*
  COMPONENTS
*/

export const FileActionsMenu: React.FC<{
  file: VaultFile
  className?: string
}> = ({ file, className }) => {
  const { selectedFileIds } = useVaultContext()
  const hidden = file.status === DocumentStatus.New || !!selectedFileIds.size
  const { value: show, setTrue: showConfirm, setFalse: hideConfirm } = useStateBoolean()
  const [isOpen, setIsOpen] = React.useState(false)

  const {
    settings: { vaultListViewEnabled },
  } = useUserSettings()

  if (hidden) return null

  return (
    <>
      <Menu onOpenChange={setIsOpen}>
        {vaultListViewEnabled ? (
          <EllipsisMenuTrigger size="medium" inert={hidden} />
        ) : (
          <StyledMenuTrigger
            className={stringHelper.classNames(className, "vault-item-menu", isOpen && "visible")}
            $hidden={hidden}
            size="small"
            variant="white"
            inert={hidden}
          />
        )}

        <MenuContent small={!vaultListViewEnabled}>
          {!vaultListViewEnabled && <DownloadFile file={file} />}
          <DownloadCSV file={file} />
          <MenuItemSeparator />
          <MenuItem label="Delete file…" Icon={SvgTrash01} onSelect={showConfirm} />
        </MenuContent>
      </Menu>

      <DeleteFileConfirm file={file} show={show} hideConfirm={hideConfirm} />
    </>
  )
}

const DownloadFile: React.FC<{
  file: VaultFile
}> = ({ file }) => {
  const downloadFile = useDownloadVaultFile()

  const onDownloadFile = React.useCallback(() => downloadFile(file), [downloadFile, file])

  const fileType = React.useMemo(() => fileHelper.fileTypeDisplayName(fileTypeFor(file)), [file])

  // Cant download if there is no source format id (pre-Vault docs)
  if (!file.sourceFormat) return null

  return (
    <MenuItem
      Icon={SvgDownload01}
      label={`Download ${fileType || "file"}`}
      onSelect={onDownloadFile}
    />
  )
}

const DownloadCSV: React.FC<{
  file: VaultFile
}> = ({ file }) => {
  const csvFile = React.useMemo(
    () => file.formats.find((format) => format.formatClass === FormatClass.CSV)?.segments?.[0],
    [file.formats]
  )

  const onDownloadFile = React.useCallback(() => {
    if (!csvFile) return
    return (window.location.href = documentDownloadUrl(
      csvFile.collectionId,
      csvFile.fileId,
      csvFile.token,
      `${file.title ?? file.fileName ?? "export"}.csv`
    ))
  }, [csvFile, file.fileName, file.title])

  if (!csvFile) return null

  return <MenuItem Icon={SvgDownload01} label="Export as CSV" onSelect={onDownloadFile} />
}

export const DownloadFileButton: React.FC<{
  file: VaultFile
}> = ({ file }) => {
  const downloadFile = useDownloadVaultFile()

  const onDownloadFile = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      return downloadFile(file)
    },
    [downloadFile, file]
  )

  const fileType = React.useMemo(() => fileHelper.fileTypeDisplayName(fileTypeFor(file)), [file])

  // Cannot download if there is no source format id (pre-Vault docs)
  if (!file.sourceFormat) return null

  return (
    <DigitsTooltip content={`Download ${fileType || "file"}`}>
      <DigitsButton $circle size="medium" $variant="ghost-dark" onClick={onDownloadFile}>
        <SvgDownload01 />
      </DigitsButton>
    </DigitsTooltip>
  )
}
